import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Signature,
  UserRole,
  useUpdateAccountMutation,
  useUpdateAccountRelationMutation,
} from '~/graphql/types';
import { Mailboxes } from '~/graphql/types.client.flow';
import AccountDrawer from '../AccountDrawer';
import AccountAdministrators from '../AccountAdministrators';
import SynchronisedMailboxes from '~/components/page/Settings/components/SynchronisedMailboxes';
import AnimatedBlock from '~/components/atom/AnimatedBlock';
import SignatureContainer from '~/components/organism/SignatureContainer';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { SynchroniseEntityType } from '~/components/page/External/Nylas/types';
import usePermissions from '~/hooks/usePermissions';
import EditableText from '~/components/organism/EditableText';
import CopyTextBlock from '~/components/molecule/CopyTextBlock';
import useTrackEvent from '~/hooks/useTrackEvent';
import MFARequiredBlock from './components/MFARequiredBlock';
import formatToastMessage from '~/util/formatToastMessage';
import useAddToast from '~/hooks/useAddToast';
import Catalog from '~/Catalog';

type AccountRelation = {
  role: UserRole;
  userId: string;
};

type Props = {
  accountId: string;
  accountName: string;
  mayEdit: boolean;
  mailboxes: Mailboxes;
  signature: Signature | null;
  setErrorMsg: (msg: string | null) => void;
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
  mfaRequired: boolean;

  /** Refetch getAccount query and session hydration */
  refetch: () => Promise<any>;
};

export const text = {
  mfaUpdateError: 'Tweestaps\u00ADverificatie-instelling bijwerken mislukt',
  emailIntegration: 'E-mailintegratie',
  signature: 'Handtekening account',
  accountIdLabel: 'Account ID',
  mayEditExplanation: 'Alleen accountbeheerders kunnen e-mails synchroniseren.',
};

const AccountComponent: React.FCC<Props> = ({
  accountId,
  accountName,
  mayEdit,
  mailboxes,
  signature,
  refetch,
  startPolling,
  stopPolling,
  mfaRequired,
}) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const hasEmailPermission = usePermissions(['root.email.account']);
  const trackEvent = useTrackEvent();
  const addToast = useAddToast();

  const [updateAccount, { loading: updateAccountLoading }] =
    useUpdateAccountMutation({
      onError: () =>
        addToast([
          formatToastMessage(Catalog.genericUnknownErrorMessage, 'danger'),
        ]),
    });
  const [updateAccountRelation, { loading: updateRelationLoading }] =
    useUpdateAccountRelationMutation({
      onError: () =>
        addToast([
          formatToastMessage(Catalog.genericUnknownErrorMessage, 'danger'),
        ]),
    });

  const filteredMailboxes = mailboxes.filter(
    mailbox =>
      mailbox.__typename === 'AccountMailbox' ||
      mailbox.__typename === 'AccountMailboxAlias',
  );

  const onSuccess = ({ data, errors }) => {
    if (!errors && data) {
      void refetch();
      addToast([formatToastMessage(Catalog.genericSuccessMessage, 'success')]);
      /** ----- TRACKING: account_updated ----- */
      trackEvent({
        eventName: 'account_updated',
      });
    }
  };

  const updateAccountName = async (value: string) =>
    await updateAccount({
      variables: {
        accountId,
        update: {
          name: value,
        },
      },
    }).then(onSuccess);

  const updateMfaRequired = async (value: boolean): Promise<void> => {
    if (!mayEdit) return;

    await updateAccount({
      variables: {
        accountId,
        update: {
          mfaRequired: value,
        },
      },
    }).then(onSuccess);
  };

  const updateUserRole = async ({ role, userId }: AccountRelation) =>
    await updateAccountRelation({
      variables: {
        accountId,
        userId,
        update: {
          role,
        },
      },
    }).then(({ data, errors }) => {
      if (!errors && data) {
        void refetch();
      }
    });

  return (
    <React.Fragment>
      <AccountDetailsForm data-testid="accounts-page">
        <JustificationContainer
          direction="column"
          gap="xxs"
          margin={[null, null, 'xxl', null]}
        >
          <EditableText
            readonly={!mayEdit}
            onSave={updateAccountName}
            text={accountName}
            loading={false}
          />
          <CopyTextBlock textToCopy={accountId} label={text.accountIdLabel} />
        </JustificationContainer>
        <JustificationContainer
          justification="space-between"
          wrap="wrap"
          width="100%"
        >
          <AccountAdministrators
            loading={updateRelationLoading}
            handleToggleDrawer={setOpenDrawer}
            updateUserRole={updateUserRole}
            isAdmin={mayEdit}
          />
          <AccountDrawer
            handleToggleDrawer={setOpenDrawer}
            updateUserRole={updateUserRole}
            isOpen={openDrawer}
            loading={updateRelationLoading}
          />

          <div>
            <MFARequiredBlock
              mfaRequired={mfaRequired}
              onSave={updateMfaRequired}
              loading={updateAccountLoading}
              disabled={!mayEdit}
            />

            {hasEmailPermission.allowed && (
              <AnimatedBlock title={text.emailIntegration}>
                <SynchronisedMailboxes
                  mailboxes={filteredMailboxes}
                  entityType={SynchroniseEntityType.Account}
                  entityId={accountId}
                  mayEdit={mayEdit}
                  mayEditExplanation={text.mayEditExplanation}
                  refetch={refetch}
                  startPolling={startPolling}
                  stopPolling={stopPolling}
                />
              </AnimatedBlock>
            )}

            {hasEmailPermission.allowed && (
              <SignatureContainer
                title={text.signature}
                signature={signature}
                refetch={refetch}
                readOnly={!mayEdit}
              />
            )}
          </div>
        </JustificationContainer>
      </AccountDetailsForm>
    </React.Fragment>
  );
};

const AccountDetailsForm = styled.section<{}>`
  width: 100%;
`;

export default AccountComponent;
